import { Button, Card, Form, Input, Select } from 'antd';

import apiBanksCall from 'apps/catalog/apiCalls/bank/list';
import apiProvincesCall from 'apps/investcore/apiCalls/provinces/list';
import apiDistrictsCall from 'apps/investment/apiCalls/data/districts';
import apiWardsCall from 'apps/investment/apiCalls/data/wards';
import {
  cardTypeOptions,
  genderOptions,
  nationalityOptions,
} from 'apps/investment/constants/user';
import UIDatePicker from 'common/ui/UIDatePicker';
import { useFetchApiList } from 'hooks/api';
import { useEffect, useRef } from 'react';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';
import { convertDateFormData, ensureDateFields } from 'utils/formData';
import {
  createVietnameseFilter,
  vietnameseFilterOption,
} from 'utils/vietnamese-search';

const { Option } = Select;

const dateFields = ['date_of_birth', 'issue_date', 'expiry'];
const nullOption = { name: 'Không có', id: null };

const addressVersionOptions = [
  { label: 'Địa chỉ cũ', value: 'v1' },
  { label: 'Địa chỉ mới (Phường/Xã + Tỉnh/Thành Phố)', value: 'v2' },
];

type FormTypes = {
  residential_address_city_id?: number;
  address_city_id?: number;
  residential_address_version?: string;
  address_version?: string;
} & Record<string, number | string | undefined | null>;

const ItemForm = ({
  form,
  submiting,
  onFinish,
  initialValues,
  name,
  params,
}) => {
  const {
    residential_address_city_id,
    address_city_id,
    residential_address_version,
    address_version,
  } = Form.useWatch<FormTypes>([], form) || {};

  const isResidentialV2 = residential_address_version === 'v2';
  const isAddressV2 = address_version === 'v2';

  // Prevent version-change effects from resetting fields during initial load
  const skipVersionReset = useRef({ residential: false, address: false });

  useEffect(() => {
    if (initialValues) {
      skipVersionReset.current = { residential: true, address: true };
      form.setFieldsValue(
        ensureDateFields(
          {
            ...initialValues,
            residential_address_version:
              initialValues?.residential_address_version || 'v1',
            address_version: initialValues?.address_version || 'v1',
          },
          dateFields
        )
      );
    }
  }, [form, initialValues]);

  const { data: banks, load: loadBanks } = useFetchApiList(apiBanksCall, {
    resourceName: 'data',
  });

  const {
    data: provinces,
    isLoading: isProvincesLoading,
    load: loadProvinces,
  } = useFetchApiList(apiProvincesCall, {
    resourceName: 'data',
  });

  const {
    data: residentialDistricts = [],
    load: loadResidentialDistricts,
  } = useFetchApiList(apiDistrictsCall('residential_address'), {
    resourceName: 'data',
  });

  const { data: districts = [], load: loadDistricts } = useFetchApiList(
    apiDistrictsCall('address'),
    {
      resourceName: 'data',
    }
  );

  const {
    data: residentialWards = [],
    load: loadResidentialWards,
  } = useFetchApiList(apiWardsCall('residential_address'), {
    resourceName: 'data',
  });

  const { data: wards = [], load: loadWards } = useFetchApiList(
    apiWardsCall('address'),
    { resourceName: 'data' }
  );

  useEffect(() => {
    loadBanks({ page_size: 99 });
    loadProvinces({ page_size: 200 });

    // Load districts/wards on mount if initial values have city selected
    if (initialValues?.residential_address_city_id) {
      const isV2 = initialValues.residential_address_version === 'v2';
      if (isV2) {
        loadResidentialWards({
          province_id: initialValues.residential_address_city_id,
          page_size: 99,
        });
      } else {
        loadResidentialDistricts({
          province_id: initialValues.residential_address_city_id,
          page_size: 99,
        });
      }
    }

    if (initialValues?.address_city_id) {
      const isV2 = initialValues.address_version === 'v2';
      if (isV2) {
        loadWards({
          province_id: initialValues.address_city_id,
          page_size: 99,
        });
      } else {
        loadDistricts({
          province_id: initialValues.address_city_id,
          page_size: 99,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (residential_address_version === undefined) return;
    if (skipVersionReset.current.residential) {
      skipVersionReset.current.residential = false;
      return;
    }
    form.setFieldsValue({
      residential_address_city_id: null,
      residential_address_district_id: null,
      residential_address_ward_id: null,
    });
  }, [residential_address_version]);

  useEffect(() => {
    if (address_version === undefined) return;
    if (skipVersionReset.current.address) {
      skipVersionReset.current.address = false;
      return;
    }
    form.setFieldsValue({
      address_city_id: null,
      address_district_id: null,
      address_ward_id: null,
    });
  }, [address_version]);

  useEffect(() => {
    if (
      residential_address_city_id !== null &&
      residential_address_city_id !== undefined
    ) {
      if (isResidentialV2) {
        loadResidentialWards({
          province_id: residential_address_city_id,
          page_size: 99,
        });
      } else {
        loadResidentialDistricts({
          province_id: residential_address_city_id,
          page_size: 99,
        });
      }
    }
  }, [residential_address_city_id, isResidentialV2]);

  useEffect(() => {
    if (address_city_id !== null && address_city_id !== undefined) {
      if (isAddressV2) {
        loadWards({ province_id: address_city_id, page_size: 99 });
      } else {
        loadDistricts({ province_id: address_city_id, page_size: 99 });
      }
    }
  }, [address_city_id, isAddressV2]);

  // Helper function to ensure selected item is in options
  const ensureSelectedInOptions = (options, selectedId, initialItem) => {
    if (!selectedId || !Array.isArray(options)) return options;

    const hasSelected = options.some(opt => opt.id === selectedId);
    if (!hasSelected && initialItem?.id === selectedId && initialItem?.name) {
      return [initialItem, ...options];
    }
    return options;
  };

  const residentialProvinceOptions = ensureSelectedInOptions(
    Array.isArray(provinces)
      ? provinces.filter(p => p.address_version === residential_address_version)
      : [],
    initialValues?.residential_address_city_id,
    initialValues?.residential_address_city
  );

  const addressProvinceOptions = ensureSelectedInOptions(
    Array.isArray(provinces)
      ? provinces.filter(p => p.address_version === address_version)
      : [],
    initialValues?.address_city_id,
    initialValues?.address_city
  );

  // Ensure selected items are in options
  const residentialDistrictOptions = ensureSelectedInOptions(
    residentialDistricts,
    initialValues?.residential_address_district_id,
    initialValues?.residential_address_district
  );

  const residentialWardOptions = ensureSelectedInOptions(
    residentialWards,
    initialValues?.residential_address_ward_id,
    initialValues?.residential_address_ward
  );

  const districtOptions = ensureSelectedInOptions(
    districts,
    initialValues?.address_district_id,
    initialValues?.address_district
  );

  const wardOptions = ensureSelectedInOptions(
    wards,
    initialValues?.address_ward_id,
    initialValues?.address_ward
  );

  const handleFinish = values => {
    const data = { ...convertDateFormData(values, dateFields) };
    if (data.residential_address_version === 'v2') {
      data.residential_address_district_id = null;
    } else {
      data.residential_address_ward_id = null;
    }
    if (data.address_version === 'v2') {
      data.address_district_id = null;
    } else {
      data.address_ward_id = null;
    }
    onFinish(data);
  };

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'user_form'}
      onFinish={handleFinish}
      scrollToFirstError
    >
      <Form.Item name="user_id" label="Mã người dùng">
        <Input placeholder="" readOnly disabled />
      </Form.Item>

      <Form.Item name="card_type" label="Loại giấy tờ định danh">
        <Select>
          {cardTypeOptions &&
            cardTypeOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Form.Item>
      <Form.Item name="nationality" label="Quốc tịch">
        <Select>
          {nationalityOptions &&
            nationalityOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Form.Item>
      <Form.Item name="id_number" label="Số định danh cá nhân">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="full_name" label="Full Name">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="date_of_birth" label="Ngày tháng năm sinh">
        <UIDatePicker placeholder="" />
      </Form.Item>

      <Form.Item name="gender" label="Giới tính">
        <Select>
          {genderOptions &&
            genderOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Form.Item>

      <Form.Item name="issue_date" label="Ngày cấp">
        <UIDatePicker placeholder="" />
      </Form.Item>

      <Form.Item name="expiry" label="Ngày hết hạn">
        <UIDatePicker placeholder="" />
      </Form.Item>

      <Form.Item name="issue_place" label="Nơi cấp">
        <Input placeholder="" />
      </Form.Item>
      <Form.Item name="original_address" label="Quê quán">
        <Input placeholder="" />
      </Form.Item>

      <Card title={'Địa chỉ thường trú'} className="mb-4">
        <Form.Item name="residential_address_version" label="Phiên bản địa chỉ">
          <Select
            options={addressVersionOptions}
            placeholder="Chọn phiên bản"
          />
        </Form.Item>

        <Form.Item name="residential_address" label="Địa chỉ">
          <Input placeholder="" />
        </Form.Item>

        <Form.Item name="residential_address_city_id" label="Tỉnh/Thành phố">
          <Select
            showSearch
            loading={isProvincesLoading}
            placeholder="Search and Select"
            optionFilterProp="name"
            options={residentialProvinceOptions}
            filterOption={vietnameseFilterOption}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        </Form.Item>

        {!isResidentialV2 && (
          <Form.Item name="residential_address_district_id" label="Quận/Huyện">
            <Select
              showSearch
              optionFilterProp="name"
              options={[
                nullOption,
                ...(Array.isArray(residentialDistrictOptions)
                  ? residentialDistrictOptions
                  : []),
              ]}
              filterOption={vietnameseFilterOption}
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder="Search and Select"
            />
          </Form.Item>
        )}

        {isResidentialV2 && (
          <Form.Item name="residential_address_ward_id" label="Phường/Xã">
            <Select
              showSearch
              optionFilterProp="name"
              options={[
                ...(Array.isArray(residentialWardOptions)
                  ? residentialWardOptions
                  : []),
              ]}
              filterOption={vietnameseFilterOption}
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder="Search and Select"
            />
          </Form.Item>
        )}
      </Card>

      <Card title={'Địa chỉ liên hệ'} className="mb-4">
        <Form.Item name="address_version" label="Phiên bản địa chỉ">
          <Select
            options={addressVersionOptions}
            placeholder="Chọn phiên bản"
          />
        </Form.Item>

        <Form.Item name="address" label="Địa chỉ">
          <Input placeholder="" />
        </Form.Item>

        <Form.Item name="address_city_id" label="Tỉnh/Thành phố">
          <Select
            showSearch
            loading={isProvincesLoading}
            placeholder="Search and Select"
            optionFilterProp="name"
            options={addressProvinceOptions}
            filterOption={vietnameseFilterOption}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        </Form.Item>

        {!isAddressV2 && (
          <Form.Item name="address_district_id" label="Quận/Huyện">
            <Select
              showSearch
              optionFilterProp="name"
              options={[
                nullOption,
                ...(Array.isArray(districtOptions) ? districtOptions : []),
              ]}
              filterOption={vietnameseFilterOption}
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder="Search and Select"
            />
          </Form.Item>
        )}

        {isAddressV2 && (
          <Form.Item name="address_ward_id" label="Phường/Xã">
            <Select
              showSearch
              optionFilterProp="name"
              options={Array.isArray(wardOptions) ? wardOptions : []}
              filterOption={vietnameseFilterOption}
              fieldNames={{ label: 'name', value: 'id' }}
              placeholder="Search and Select"
            />
          </Form.Item>
        )}
      </Card>

      <Form.Item name="bank_id" label="Ngân hàng">
        <Select
          showSearch
          optionFilterProp="name"
          options={banks}
          fieldNames={{ label: 'name', value: 'id' }}
          placeholder="Select Bank"
          filterOption={createVietnameseFilter(['name', 'code'])}
        />
      </Form.Item>

      <Form.Item name="bank_account_number" label="STK Ngân hàng">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="bank_account_name" label="Tên tài khoản ngân hàng">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="bank_branch_name" label="Chi nhánh ngân hàng">
        <Input placeholder="" />
      </Form.Item>
      <Form.Item name="reviewer_notes" label="Ghi chú">
        <Input.TextArea rows={6} />
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ItemForm;
